import Add from "@mui/icons-material/Add";
import Cancel from "@mui/icons-material/Cancel";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Close from "@mui/icons-material/Close";
import ExpandMore from "@mui/icons-material/ExpandMore";
import PersonAdd from "@mui/icons-material/PersonAdd";
import {
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Tooltip,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { hasValue } from "encamp-shared/src/utils/hasValue";
import { AssignmentType, Permission, TierIiReportOrgStatus } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useFacilityReportEditable } from "hooks/useFacilityReportEditable";
import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import invariant from "tiny-invariant";

import { ReportRow } from ".";
import { AssigneesEditorDialog, AssignmentAction } from "./AssigneesEditor";
import { CreateAnnualReportDialog } from "./CreateAnnualReportDialog";
import { MarkReportingModal } from "./MarkReportingModal";
import { NotReportingModal } from "./NotReportingModal";

type ReportBulkActionsMenuProps = {
  reports: ReportRow[];
  onClearSelections?: () => void; // Callback to clear grid selections
};

const ReportBulkActionsMenu: React.FC<ReportBulkActionsMenuProps> = function ({
  reports,
  onClearSelections,
}) {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [showNotReportingModal, setShowNotReportingModal] = useState(false);
  const [markReportingModalOpen, setMarkReportingModalOpen] = useState(false);
  const [showAssigneeModal, setShowAssigneeModal] = useState<
    [AssignmentType, AssignmentAction] | undefined
  >(undefined);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showCreateAnnualReportDialog, setShowCreateAnnualReportDialog] = useState(false);
  const { hasPermissionForFacility } = useAuthorization();

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const alerts = useAlerts();

  const handleOpenAnnualReportDialog = useCallback(() => {
    handleClose();
    setShowCreateAnnualReportDialog(true);
  }, []);

  const { canAddAssigneesOrReviewers, canMarkAsNotReporting, canMarkAsReporting } =
    useFacilityReportEditable();

  invariant(tenantId);

  const {
    canAddAnyAssigneesOrReviewers,
    canMarkAnyAsNotReporting,
    canMarkAnyAsReporting,
    isAnnualEnabled,
    markNotReportingText,
    hasMixedReportingYears,
    mixedYearsTooltip,
  } = useMemo(() => {
    const canVerifyAndSubmit = reports.every((r) =>
      hasPermissionForFacility(r.facilityId, [Permission.VerifyTierIiReport])
    );
    const isAnnualEnabled =
      canVerifyAndSubmit && reports.some((r) => r.organizationStatusAnnual === null);
    const canAddAnyAssigneesOrReviewers = reports.some((r) => canAddAssigneesOrReviewers(r));
    const canMarkAnyAsReporting = reports.some((r) => canMarkAsReporting(r));
    const reportsThatCanBeMarkedAsNotReporting = reports.filter((r) => canMarkAsNotReporting(r));
    const markNotReportingText = reportsThatCanBeMarkedAsNotReporting.some(
      (r) => r.organizationStatus !== TierIiReportOrgStatus.NotReporting
    )
      ? "Mark as Not Reporting"
      : "Edit Not Reporting Reason";
    const canMarkAnyAsNotReporting = reportsThatCanBeMarkedAsNotReporting.length > 0;

    // Check for mixed reporting years
    const reportingYears = new Set(reports.map((r) => r.reportingYear));
    const hasMixedReportingYears = reportingYears.size > 1;
    const mixedYearsTooltip = hasMixedReportingYears
      ? `Bulk actions are not available when reports have different reporting years (${Array.from(
          reportingYears
        )
          .sort()
          .join(", ")})`
      : "";

    return {
      canAddAnyAssigneesOrReviewers,
      canMarkAnyAsNotReporting,
      canMarkAnyAsReporting,
      isAnnualEnabled,
      markNotReportingText,
      hasMixedReportingYears,
      mixedYearsTooltip,
    };
  }, [
    reports,
    canMarkAsReporting,
    canAddAssigneesOrReviewers,
    canMarkAsNotReporting,
    hasPermissionForFacility,
  ]);

  // Helper function to wrap menu items with tooltip when disabled due to mixed years
  const wrapWithTooltip = (menuItem: React.ReactElement, disabled: boolean) => {
    if (disabled && hasMixedReportingYears) {
      return (
        <Tooltip key={menuItem.key} title={mixedYearsTooltip} placement="left">
          <span>{menuItem}</span>
        </Tooltip>
      );
    }
    return menuItem;
  };

  const menuItems = [
    {
      items: [
        isAnnualEnabled &&
          wrapWithTooltip(
            <MenuItem
              key="createAnnualReport"
              disabled={reports.every((r) => hasValue(r.tierIIReportId)) || hasMixedReportingYears}
              onClick={handleOpenAnnualReportDialog}
            >
              <ListItemIcon>
                <Add />
              </ListItemIcon>
              <ListItemText>Create Annual Report</ListItemText>
            </MenuItem>,
            hasMixedReportingYears
          ),
      ].filter(Boolean),
    },
    {
      items: canAddAnyAssigneesOrReviewers
        ? [
            wrapWithTooltip(
              <MenuItem
                key="addAssignees"
                disabled={hasMixedReportingYears}
                onClick={() => {
                  setShowAssigneeModal([AssignmentType.Assignee, AssignmentAction.Assign]);
                }}
              >
                <ListItemIcon>
                  <PersonAdd fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add Assignees</ListItemText>
              </MenuItem>,
              hasMixedReportingYears
            ),
            wrapWithTooltip(
              <MenuItem
                key="addReviewers"
                disabled={hasMixedReportingYears}
                onClick={() => {
                  setShowAssigneeModal([AssignmentType.Reviewer, AssignmentAction.Assign]);
                }}
              >
                <ListItemIcon>
                  <PersonAdd fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add Reviewers</ListItemText>
              </MenuItem>,
              hasMixedReportingYears
            ),
            <Divider key="divider-1" />,
            wrapWithTooltip(
              <MenuItem
                key="removeAssignees"
                disabled={
                  reports.flatMap((r) => r.assignees).length === 0 || hasMixedReportingYears
                }
                onClick={() => {
                  setShowAssigneeModal([AssignmentType.Assignee, AssignmentAction.Remove]);
                }}
              >
                <ListItemIcon>
                  <Close fontSize="small" />
                </ListItemIcon>
                <ListItemText>Remove Assignees</ListItemText>
              </MenuItem>,
              hasMixedReportingYears
            ),
            wrapWithTooltip(
              <MenuItem
                key="removeReviewers"
                disabled={
                  reports.flatMap((r) => r.reviewers).length === 0 || hasMixedReportingYears
                }
                onClick={() => {
                  setShowAssigneeModal([AssignmentType.Reviewer, AssignmentAction.Remove]);
                }}
              >
                <ListItemIcon>
                  <Close fontSize="small" />
                </ListItemIcon>
                <ListItemText>Remove Reviewers</ListItemText>
              </MenuItem>,
              hasMixedReportingYears
            ),
          ]
        : [],
    },
    {
      items: [
        canMarkAnyAsNotReporting &&
          wrapWithTooltip(
            <MenuItem
              key="markNotReporting"
              disabled={hasMixedReportingYears}
              onClick={() => setShowNotReportingModal(true)}
            >
              <ListItemIcon>
                <Cancel fontSize="small" />
              </ListItemIcon>
              <ListItemText>{markNotReportingText}</ListItemText>
            </MenuItem>,
            hasMixedReportingYears
          ),
        canMarkAnyAsReporting &&
          wrapWithTooltip(
            <MenuItem
              key="markReporting"
              disabled={hasMixedReportingYears}
              onClick={() => setMarkReportingModalOpen(true)}
            >
              <ListItemIcon>
                <CheckCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText>Mark as Reporting</ListItemText>
            </MenuItem>,
            hasMixedReportingYears
          ),
      ].filter(Boolean),
    },
  ];

  const shouldShowDivider = (index: number) => {
    // Check if the current group has items and if any of the remaining groups have items
    return (
      menuItems[index].items.length > 0 &&
      menuItems.slice(index + 1).some((group) => group.items.length > 0)
    );
  };

  const showMenu = menuItems.some((group) => group.items.length > 0);

  const disabled = reports.length === 0 || !showMenu;

  return (
    <>
      <Button
        id="report-bulk-actions-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        variant="contained"
        disabled={disabled}
        onClick={handleClick}
        endIcon={<ExpandMore />}
      >
        Bulk Actions
      </Button>
      <Menu
        id="report-bulk-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "report-bulk-actions-button",
        }}
      >
        <MenuList dense>
          {menuItems.map((group, index) => (
            <React.Fragment key={index}>
              {group.items}
              {shouldShowDivider(index) && <Divider />}
            </React.Fragment>
          ))}
        </MenuList>
      </Menu>
      {!!showAssigneeModal && (
        <AssigneesEditorDialog
          assignmentType={showAssigneeModal[0]}
          assignmentAction={showAssigneeModal[1]}
          onClose={() => setShowAssigneeModal(undefined)}
          tierIIReports={reports.filter((r) => canAddAssigneesOrReviewers(r))}
          tenantId={tenantId}
        />
      )}
      {showNotReportingModal && (
        <NotReportingModal
          open={showNotReportingModal}
          onClose={() => setShowNotReportingModal(false)}
          onSuccess={onClearSelections}
          reports={reports}
        />
      )}
      {markReportingModalOpen && (
        <MarkReportingModal
          onClose={() => setMarkReportingModalOpen(false)}
          onSuccess={onClearSelections}
          reports={reports}
        />
      )}
      {showCreateAnnualReportDialog && (
        <CreateAnnualReportDialog
          onClose={() => setShowCreateAnnualReportDialog(false)}
          reports={reports}
        />
      )}
    </>
  );
};

export { ReportBulkActionsMenu };
